import { useCallback, useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { useQueries } from '@tanstack/react-query'
import Box from '@/components/common/Box'
import { useAppDispatch, useAppSelector } from '@/store'
import {
	selectCartItems,
	selectCalculations,
	selectOrderTypeAmountAlert,
	selectOrderTypeMinAlertNudge,
	selectSelectedOrderTypeGuid,
	removeItem,
	updateItemQuantity,
	calculate,
	resetOrderTypeMinWarning,
} from '@/store/ticketSlice'
import { useLocationRef } from '@/contexts/RefContext'
import { getMenuItemDetail } from '@/shared/api/menu'
import type { MenuItem, MenuItemDetail } from '@/shared/api/menu'
import type { CartItem } from '@/shared/types/cart'
import { useFormatMessage } from '@/translations/useFormatMessage'
import { CartItemRow } from './CartItemRow'
import { CartActionsFooter } from './CartActionsFooter'
import { OrderTypeMinAlert } from './OrderTypeMinAlert'
import { AmountDueAccordion } from './AmountDueAccordion'
import {
	PanelRoot,
	PanelHeader,
	PanelHeading,
	PanelHeadingCount,
	ItemList,
	EmptyState,
	EmptyStateText,
	OrderSummaryFooter,
} from './OrderDetailsPanel.styled'

interface OrderDetailsPanelProps {
	menuItems: Record<string, MenuItem>
	currency?: string
	hideHeader?: boolean
	timeZoneOffset?: number
}

export function OrderDetailsPanel({
	menuItems,
	currency = 'USD',
	hideHeader = false,
	timeZoneOffset,
}: OrderDetailsPanelProps) {
	const dispatch = useAppDispatch()
	const intl = useIntl()
	const { t } = useFormatMessage()
	const locationRef = useLocationRef()

	const items = useAppSelector(selectCartItems)
	const itemCount = items.reduce((sum, item) => sum + item.quantity, 0)
	const calculations = useAppSelector(selectCalculations)
	const amountAlert = useAppSelector(selectOrderTypeAmountAlert)
	const nudgeKey = useAppSelector(selectOrderTypeMinAlertNudge)
	const selectedOrderTypeGuid = useAppSelector(selectSelectedOrderTypeGuid)
	const isDineIn = selectedOrderTypeGuid === 'DINE_IN'

	// Ember parity (menu.js model() → store.query('item', { schedule }) populating the identity map):
	// After a page refresh the TanStack Query cache is cold, so modifier names cannot be resolved
	// from cache alone. Fire a MenuItemDetail fetch for every cart item that has modifiers.
	// useQueries deduplicates by queryKey, so each item is fetched at most once per session.
	// staleTime: 0 matches AddItemPage's own staleTime — Ember used reload:true on every visit.
	const detailQueries = useQueries({
		queries: items
			.filter((item) => item.modifiers.length > 0)
			.map((item) => ({
				queryKey: ['menu-item-detail', locationRef, item.itemRef, isDineIn] as const,
				queryFn: () => getMenuItemDetail(locationRef, item.itemRef, isDineIn),
				staleTime: 0,
			})),
	})

	// Build itemRef → MenuItemDetail map from completed queries.
	const menuItemDetails: Record<string, MenuItemDetail> = {}
	for (const result of detailQueries) {
		if (result.data) {
			menuItemDetails[result.data.ref] = result.data
		}
	}

	const [announcement, setAnnouncement] = useState('')

	// Debounced calculate — coalesces rapid +/− taps into one API call.
	// The in-flight guard in ticketSlice drops concurrent calls, so without
	// debouncing the final quantity after rapid taps never gets sent.
	// 400 ms matches typical double-tap timing without feeling sluggish.
	// If the timer fires while a request is already in-flight, ticketSlice's
	// cart-revision check detects the stale response on completion and
	// automatically re-fires calculate() — no follow-up needed here.
	const calculateTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

	const debouncedCalculate = useCallback(() => {
		if (calculateTimerRef.current !== null) {
			clearTimeout(calculateTimerRef.current)
		}
		calculateTimerRef.current = setTimeout(() => {
			calculateTimerRef.current = null
			void dispatch(calculate())
		}, 400)
	}, [dispatch])

	useEffect(() => {
		return () => {
			if (calculateTimerRef.current !== null) {
				clearTimeout(calculateTimerRef.current)
			}
		}
	}, [])

	// Arrow Up/Down navigate between cart item rows and preventDefault so the
	// underlying menu page does not scroll (WCAG 2.1 AA — keyboard trap avoidance).
	// When idx === -1 (focus is outside the list), ArrowDown goes to first row and
	// ArrowUp goes to last row — consistent with standard listbox behaviour.
	function handleItemListKeyDown(e: React.KeyboardEvent<HTMLElement>) {
		if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return
		const list = e.currentTarget
		const rows = Array.from(
			list.querySelectorAll<HTMLElement>('[data-testid="cart-item-row-group"]'),
		)
		if (rows.length === 0) return
		const activeGroup = (document.activeElement as HTMLElement | null)?.closest<HTMLElement>(
			'[data-testid="cart-item-row-group"]',
		)
		const activeEditBtn = (document.activeElement as HTMLElement | null)?.closest<HTMLElement>(
			'[data-testid="cart-item-edit-btn"]',
		)
		// If focus is anywhere inside the group but NOT on the overlay edit button (e.g. qty buttons
		// or the modifier toggle), let the browser handle the key — do not steal it for navigation.
		if (activeGroup && document.activeElement !== activeEditBtn) return
		e.preventDefault()
		const idx = activeGroup ? rows.indexOf(activeGroup) : -1
		const targetGroup =
			e.key === 'ArrowDown'
				? rows[idx === -1 ? 0 : (idx + 1) % rows.length]
				: rows[idx === -1 ? rows.length - 1 : (idx - 1 + rows.length) % rows.length]
		targetGroup?.querySelector<HTMLElement>('[data-testid="cart-item-edit-btn"]')?.focus()
	}

	function handleRemove(item: CartItem) {
		const menuItem = menuItems[item.itemRef]
		const name = menuItem?.name ?? item.itemRef
		dispatch(removeItem(item.id))
		dispatch(resetOrderTypeMinWarning())
		void dispatch(calculate())
		setAnnouncement(t('component:cartItemRow:removed:announcement', { name }))
	}

	function handleIncrement(item: CartItem) {
		dispatch(updateItemQuantity({ itemId: item.id, quantity: item.quantity + 1 }))
		debouncedCalculate()
	}

	function handleDecrement(item: CartItem) {
		dispatch(updateItemQuantity({ itemId: item.id, quantity: item.quantity - 1 }))
		dispatch(resetOrderTypeMinWarning())
		debouncedCalculate()
	}

	return (
		<PanelRoot>
			{/* ARIA live region — announced on item removal */}
			<Box
				role="status"
				aria-live="polite"
				aria-atomic="true"
				sx={{
					position: 'absolute',
					width: 1,
					height: 1,
					overflow: 'hidden',
					clip: 'rect(0,0,0,0)',
				}}
			>
				{announcement}
			</Box>

			{!hideHeader && (
				<PanelHeader>
					<PanelHeading>
						{t('component:orderDetailsPanel:heading:label')}{' '}
						<PanelHeadingCount>({itemCount})</PanelHeadingCount>
					</PanelHeading>
				</PanelHeader>
			)}

			{items.length === 0 ? (
				<EmptyState>
					<svg
						width={128}
						height={128}
						viewBox="0 0 128 128"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
						aria-hidden="true"
					>
						<path
							d="M35.9295 101.863C21.1327 101.863 9.08789 89.8181 9.08789 75.0213C9.08789 60.2245 21.1327 48.1797 35.9295 48.1797C50.7263 48.1797 62.7711 60.2245 62.7711 75.0213C62.7711 89.8181 50.7263 101.863 35.9295 101.863ZM35.9295 50.7269C22.5407 50.7269 11.6479 61.6197 11.6479 75.0085C11.6479 88.3973 22.5407 99.2901 35.9295 99.2901C49.3183 99.2901 60.2111 88.3973 60.2111 75.0085C60.2111 61.6197 49.3183 50.7269 35.9295 50.7269Z"
							fill="black"
						/>
						<path
							d="M32.8196 78.5801C32.7428 75.3289 34.3044 74.1257 36.442 73.3321L37.6708 72.8969C39.2964 72.2825 40.6789 70.8745 40.6789 68.8777C40.6789 66.2665 38.9764 64.7945 36.7365 64.7945C34.4965 64.7945 32.4356 66.4201 32.282 69.3513H29.2741C29.2741 64.7561 32.8581 61.8633 36.7236 61.8633C40.5893 61.8633 43.8148 64.5769 43.8148 68.8777C43.8148 72.3849 41.3957 74.9961 38.5669 75.8537C37.0436 76.3273 35.9685 76.8265 35.9685 78.5673L35.93 80.0137H32.858L32.8196 78.5673V78.5801ZM32.0644 85.3385C32.0644 84.0713 33.114 83.1369 34.3813 83.1369C35.6485 83.1369 36.698 84.0713 36.698 85.3385C36.698 86.6057 35.6485 87.5401 34.3813 87.5401C33.114 87.5401 32.0644 86.6057 32.0644 85.3385ZM68.9284 115.086H16.7812V93.2873H19.3412V112.526H68.9284V115.086Z"
							fill="black"
						/>
						<path
							d="M70.2074 105.447H67.6474V58.5985H79.9482C80.1018 55.6417 81.2282 49.9969 89.8298 46.6433V32.1538H72.1018V39.1553H69.5418V32.1538H39.641V39.1553H37.081V32.1538H19.353V56.7426H16.793V29.5938H92.4026V48.4354L91.545 48.7426C82.4826 51.9426 82.4954 57.4082 82.4954 59.7506V61.1586H70.2202V105.447H70.2074Z"
							fill="black"
						/>
						<path
							d="M72.1924 40.436H69.4276V30.8744C69.4276 22.5032 62.7588 15.6808 54.5796 15.6808C46.4004 15.6808 39.7316 22.4904 39.7316 30.8744V40.436H36.9668V30.8744C36.9668 20.98 44.8644 12.916 54.5796 12.916C64.2948 12.916 72.1924 20.9672 72.1924 30.8744V40.436ZM118.912 115.086H67.6612V58.6632H118.912V115.086ZM70.2084 112.526H116.34V61.2232H70.2084V112.526Z"
							fill="black"
						/>
						<path
							d="M106.841 69.5164H104.076V60.3772C104.076 54.5916 100.044 49.702 94.7065 48.998C91.6089 48.6012 88.6137 49.51 86.2713 51.558C83.8649 53.67 82.4953 56.7164 82.4953 59.942V69.5036H79.7305V60.3772C79.7305 53.1964 84.7865 47.1292 91.5065 46.246C95.3977 45.734 99.1737 46.886 102.118 49.4716C103.603 50.7816 104.792 52.3928 105.607 54.1981C106.421 56.0035 106.842 57.9615 106.841 59.942V69.5164Z"
							fill="black"
						/>
					</svg>
					<EmptyStateText>
						{t('component:orderDetailsPanel:emptyCart:label')}
					</EmptyStateText>
				</EmptyState>
			) : (
				<ItemList
					role="list"
					aria-label={t('component:orderDetailsPanel:heading:label')}
					onKeyDown={handleItemListKeyDown}
					data-testid="cart-item-list"
				>
					{items.map((item, index) => (
						<CartItemRow
							key={item.id}
							item={item}
							menuItem={menuItems[item.itemRef]}
							menuItemDetail={menuItemDetails[item.itemRef]}
							locationRef={locationRef}
							onRemove={handleRemove}
							onIncrement={handleIncrement}
							onDecrement={handleDecrement}
							locale={intl.locale}
							showDivider={index < items.length - 1}
						/>
					))}
				</ItemList>
			)}

			{/* Amount alert — sits between items and Order Summary, only when a constraint is active */}
			{amountAlert !== null && <OrderTypeMinAlert alert={amountAlert} nudgeKey={nudgeKey} />}

			<OrderSummaryFooter>
				<AmountDueAccordion calculations={calculations} currency={currency} />
			</OrderSummaryFooter>

			<CartActionsFooter timeZoneOffset={timeZoneOffset} currency={currency} />
		</PanelRoot>
	)
}
